import Script from 'next/script';

/**
 * A Google Ads account to configure for remarketing, together with the
 * conversion label its app-remarketing event is sent to.
 */
export interface RemarketingAccount {
  /** Partner the account belongs to; used to build the script id. */
  partner: string;
  /** Google Ads account id, e.g. `AW-1040600082`. */
  accountId: string;
  /** Conversion label the remarketing event targets, e.g. `AW-1040600082/EY5b…`. */
  conversionLabel: string;
}

export const REMARKETING_ACCOUNTS: readonly RemarketingAccount[] = [
  {
    partner: 'comcast',
    accountId: 'AW-1040600082',
    conversionLabel: 'AW-1040600082/EY5bCMqdqPscEJKYmfAD',
  },
  {
    partner: 'spectrum',
    accountId: 'AW-630422453',
    conversionLabel: 'AW-630422453/0BQtCIKflvwcELX3zawC',
  },
];

/**
 * Builds the inline gtag.js commands for one account: a `config` for the Google
 * Ads account, followed by the app-remarketing conversion event.
 *
 * The `dataLayer`/`gtag` shim mirrors the vendor snippet, which makes the script
 * independent of the order it runs in relative to the Google tag — gtag.js
 * drains whatever the queue already holds once it loads.
 *
 * The vendor snippet's `gtag('js', new Date())` is deliberately left out: it
 * stamps the library's page-load reference time, so it belongs once per page
 * rather than once per destination, and `GoogleAnalyticsProvider` already
 * issues it. Repeating it here would re-stamp that time mid-page.
 */
export const buildRemarketingSnippet = (
  account: RemarketingAccount
): string => {
  const eventParams = {
    send_to: account.conversionLabel,
    value: 0.0,
    currency: 'USD',
    aw_remarketing_only: true,
  };

  return [
    'window.dataLayer = window.dataLayer || [];',
    'window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };',
    `window.gtag('config', '${account.accountId}');`,
    `window.gtag('event', 'conversion', ${JSON.stringify(eventParams)});`,
  ].join('\n');
};

export interface RemarketingPixelsProps {
  /** `serverVariables.env`, e.g. `prod`, `prod-canary`, `staging`. */
  env: string;
  /** Accounts to configure; defaults to {@link REMARKETING_ACCOUNTS}. */
  accounts?: readonly RemarketingAccount[];
}

/**
 * Renders the Google Ads remarketing pixel and app-remarketing conversion event
 * for every configured partner account, on every page of the app.
 *
 * Relies on gtag.js already being loaded by `GoogleAnalyticsProvider`, so render
 * this directly after it. Google Ads and GA4 share a single gtag.js instance —
 * each destination is added with its own `config` call rather than its own
 * loader — so no further `gtag/js` script belongs here.
 *
 * Production only. Analytics elsewhere is pointed at a throwaway GA property,
 * but the partners issued a single set of Ads accounts with no test equivalent,
 * so the only way to keep local, review, staging and CI traffic out of the live
 * remarketing audiences is to not fire at all outside production.
 */
export const RemarketingPixels: React.FC<RemarketingPixelsProps> = ({
  env,
  accounts = REMARKETING_ACCOUNTS,
}) => {
  // Covers both `prod` and the `prod-canary` pods.
  if (!env.startsWith('prod')) {
    return null;
  }

  return (
    <>
      {accounts.map((account) => (
        <Script
          key={account.partner}
          id={`remarketing-pixel-${account.partner}`}
          dangerouslySetInnerHTML={{
            __html: buildRemarketingSnippet(account),
          }}
        />
      ))}
    </>
  );
};
